import { useState, useEffect, useCallback, useRef } from 'react';
import { holdsTypingRole } from '../utils/heldRoles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { getRoleDashboardPath } from '../utils/roleDashboard';
import { toast } from 'sonner';
import {
  Keyboard, RefreshCw, Search, Inbox, User, PlayCircle, CheckCircle2,
  Clock, Globe, Building2, Users, LogOut, ChevronDown,
  TrendingUp, SearchX, CircleDashed,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import OrderSummaryPopup from '../components/OrderSummaryPopup';
import { PriorityIndicator } from '../components/shared/StatusBadge';
import { formatDateOnlyET } from '../utils/formatters';
import { TYPING_STATUS_META as STATUS_META } from '../components/typing/TypingOrderSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TYPING-ADDR-001 — authoritative property address, consistent with the
// Operations Dashboard: prefer the backend-composed display string, else
// compose street + city + state + zip. Guards against demo/legacy rows whose
// `property_address` already embeds city/state (avoids "…, CA, CA").
const typingPropertyAddress = (o) => {
  if (!o) return '—';
  if (o.property_address_display && o.property_address_display.trim()) return o.property_address_display.trim();
  const street = (o.property_address || o.property_address_full || o.property_address_line1 || '').trim();
  const streetLc = street.toLowerCase();
  const parts = [];
  if (street) parts.push(street);
  const city = (o.property_city || '').trim();
  if (city && !streetLc.includes(city.toLowerCase())) parts.push(city);
  const state = (o.property_state || o.state || '').trim();
  const zip = (o.property_zip || o.property_zip_code || '').trim();
  const stateInStreet = state ? new RegExp(`(^|[,\\s])${state}([,\\s]|$)`, 'i').test(street) : false;
  if (state && !stateInStreet) parts.push(zip ? `${state} ${zip}` : state);
  else if (state && stateInStreet && zip && !streetLc.includes(zip)) parts.push(zip);
  return parts.filter(Boolean).join(', ') || '—';
};

const TABS = [
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'my_queue', label: 'My Queue' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed_today', label: 'Completed Today' },
  { key: 'all', label: 'All In Typing' },
];

const EMPTY_STATES = {
  unassigned: { icon: Inbox, title: 'No files waiting for Typing', hint: 'New files appear here as Search and QC complete their work.' },
  my_queue: { icon: User, title: 'Your queue is empty', hint: 'Claim a file from the Unassigned tab to get started.' },
  in_progress: { icon: CircleDashed, title: 'No files in progress', hint: 'Start a claimed file to see it here.' },
  completed_today: { icon: CheckCircle2, title: 'Nothing completed today yet', hint: 'Files you complete today will show here.' },
  all: { icon: Keyboard, title: 'No files currently in Typing', hint: 'The Typing stage is clear.' },
};

// Entered·Age column removed per UAT — typing_entered_at data remains in backend.

const MyDayStat = ({ value, label, testId }) => (
  <div className="flex items-baseline gap-1.5" data-testid={testId}>
    <span className="text-lg font-black tracking-tighter text-slate-900">{value ?? 0}</span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-500">{label}</span>
  </div>
);

export default function TypingDashboard({ embedded = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isTypist = holdsTypingRole(user); // TYPING-DUAL-ROLE-001: primary or additional role
  const [tab, setTab] = useState('unassigned');
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [workload, setWorkload] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [assignTarget, setAssignTarget] = useState('');
  const [showWorkload, setShowWorkload] = useState(false);
  const [summaryOrder, setSummaryOrder] = useState(null);
  const searchTimer = useRef(null);
  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  // TYPING-SCOPE-001: only Typing Processors and Admin belong here —
  // redirect any other role to their own dashboard instead of a blank page.
  useEffect(() => {
    if (user && !holdsTypingRole(user) && user.role !== 'admin') {
      navigate(getRoleDashboardPath(user), { replace: true });
    }
  }, [user, navigate]);

  // Debounced server-side search (works across pagination)
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ tab, page: String(page), per_page: String(perPage) });
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('typing_status', statusFilter);
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/typing/orders?${params}`, authHeader()),
        axios.get(`${API}/typing/stats`, authHeader()),
      ]);
      setOrders(oRes.data.orders || []);
      setCount(oRes.data.count || 0);
      setStats(sRes.data);
      if (isAdmin) {
        const wRes = await axios.get(`${API}/typing/workload`, authHeader());
        setWorkload(wRes.data.typists || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Typing queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab, page, perPage, search, statusFilter, isAdmin, authHeader]);

  useEffect(() => { load(); }, [load]);

  const goTab = (t, status = '') => { setTab(t); setStatusFilter(status); setPage(1); };
  const hasFilters = !!(search.trim() || statusFilter);
  const clearFilters = () => { setSearchInput(''); setSearch(''); setStatusFilter(''); setPage(1); };

  const act = async (orderId, action, body = {}) => {
    setBusy(orderId + action);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/typing/${action}`, body, authHeader());
      if (action === 'complete') toast.success('Typing completed — order finally completed and delivered');
      else if (action === 'assign-to-me') toast.success('File assigned to you — find it under My Queue');
      else if (action === 'start') toast.success('Typing started');
      else if (action === 'return') toast.success('File returned for correction');
      else if (action === 'assign' || action === 'reassign') toast.success(`Assigned to ${res.data.typing_assigned_to_name}`);
      else toast.success('Done');
      setAssignModal(null); setAssignTarget('');
      // TYPING-START-DASH-001: Start Typing lives on the queue only — after a
      // successful start, land the typist on the order (already In Progress).
      if (action === 'start') { navigate(`/orders/${orderId}`); return; }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  // KPI card — TitleFlow premium style; clickable cards filter the queue
  const statCard = (label, value, testid, Icon, iconCls, onClick, active) => (
    <button type="button" onClick={onClick}
      className={`text-left bg-white border rounded-xl px-4 py-3.5 flex items-center gap-3 shadow-sm transition-all
        ${active ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-blue-300 hover:shadow-md'}`}
      data-testid={testid}>
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconCls}`}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.05em] truncate">{label}</p>
        <p className="text-2xl font-black tracking-tighter text-slate-900 leading-tight">{value ?? '—'}</p>
      </div>
    </button>
  );

  const totalPages = Math.max(1, Math.ceil(count / perPage));
  const pageNumbers = (() => {
    const win = 5;
    let start = Math.max(1, page - Math.floor(win / 2));
    const end = Math.min(totalPages, start + win - 1);
    start = Math.max(1, end - win + 1);
    const arr = [];
    for (let p = start; p <= end; p += 1) arr.push(p);
    return arr;
  })();

  const emptyMeta = hasFilters
    ? { icon: SearchX, title: 'No results match your search', hint: 'Try different keywords or clear the filters.' }
    : (EMPTY_STATES[tab] || EMPTY_STATES.all);
  const EmptyIcon = emptyMeta.icon;

  const headerBlock = (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="h-11 w-11 rounded-xl bg-blue-600 shadow-sm flex items-center justify-center flex-shrink-0">
        <Keyboard className="h-[22px] w-[22px] text-white" />
      </span>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="typing-page-title">Typing Operations</h1>
        <p className="text-[12px] text-slate-500">Typing Queue · Final report preparation — Title Search workflow</p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setShowWorkload(v => !v)} data-testid={embedded ? 'typing-workload-toggle-embedded' : 'typing-workload-toggle'}>
            <Users className="h-3.5 w-3.5 mr-1" /> Workload <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${showWorkload ? 'rotate-180' : ''}`} />
          </Button>
        )}
        {isAdmin && !embedded && (
          <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="typing-back-admin">Admin Home</Button>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing}
          className="border-blue-200 text-blue-700 hover:bg-blue-50"
          data-testid={embedded ? 'typing-refresh-btn-embedded' : 'typing-refresh-btn'}>
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        {!embedded && <span className="text-xs text-slate-500 hidden sm:block">{user?.full_name}</span>}
        {!embedded && !isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} data-testid="typing-logout-btn"><LogOut className="h-3.5 w-3.5" /></Button>
        )}
      </div>
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'} data-testid="typing-dashboard">
      {!embedded && (
        <header className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-20">{headerBlock}</header>
      )}

      <main className={embedded ? 'space-y-4' : 'max-w-7xl mx-auto px-6 py-5 space-y-4'}>
        {embedded && headerBlock}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {statCard('Total Typing Files', stats?.total, 'typing-stat-total', Keyboard, 'bg-blue-600 text-white',
            () => goTab('all'), tab === 'all' && !statusFilter)}
          {statCard('Unassigned', stats?.unassigned, 'typing-stat-unassigned', Inbox, 'bg-amber-50 text-amber-600',
            () => goTab('unassigned'), tab === 'unassigned')}
          {statCard('Pending', stats?.pending, 'typing-stat-pending', Clock, 'bg-orange-50 text-orange-600',
            () => goTab('all', 'pending'), tab === 'all' && statusFilter === 'pending')}
          {statCard('In Progress', stats?.in_progress, 'typing-stat-in-progress', PlayCircle, 'bg-blue-50 text-blue-600',
            () => goTab('in_progress'), tab === 'in_progress')}
          {statCard('My Queue', stats?.my_queue, 'typing-stat-my-queue', User, 'bg-violet-50 text-violet-600',
            () => goTab('my_queue'), tab === 'my_queue')}
          {statCard('Completed Today', stats?.completed_today, 'typing-stat-completed-today', CheckCircle2, 'bg-emerald-50 text-emerald-600',
            () => goTab('completed_today'), tab === 'completed_today')}
        </div>

        {!isAdmin && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="typing-my-day">
            <div className="flex items-center gap-2 shrink-0">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-800">My Day</span>
            </div>
            <MyDayStat value={stats?.completed_today} label="completed today" testId="typing-myday-completed" />
            <MyDayStat value={stats?.my_queue} label="in my queue" testId="typing-myday-queue" />
            <MyDayStat value={stats?.in_progress} label="working now" testId="typing-myday-working" />
            <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid="typing-myday-target">
              Daily target: Not configured
            </span>
          </div>
        )}

        {isAdmin && showWorkload && (
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="typing-workload-panel">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Typing Processor Workload</p>
            {workload.length === 0 ? <p className="text-sm text-slate-400 italic">No typing processors found</p> : (
              <table className="w-full text-sm">
                <thead className="tf-thead"><tr className="text-left text-[11px] uppercase">
                  <th className="py-1">Typist</th><th>Assigned</th><th>In Progress</th><th>Completed Today</th><th>Completed Total</th>
                </tr></thead>
                <tbody>
                  {workload.map(w => (
                    <tr key={w.user_id} className="border-t border-slate-100" data-testid={`workload-row-${w.email}`}>
                      <td className="py-1.5 font-medium text-slate-700">{w.name} {!w.active && <span className="text-[10px] text-rose-500">(inactive)</span>}</td>
                      <td>{w.assigned ?? '—'}</td><td>{w.in_progress ?? '—'}</td><td>{w.completed_today ?? '—'}</td><td>{w.completed_total ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm">
            {TABS.map(t => (
              <button key={t.key} onClick={() => goTab(t.key)}
                className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-md transition-colors ${tab === t.key ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                data-testid={`typing-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative ml-auto flex-1 max-w-xl min-w-[280px]">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Search Order #, Product ID, client, product, address, county, state…"
              className="h-9 w-full text-sm border border-slate-200 rounded-lg pl-8 pr-3 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-white"
              data-testid="typing-search-input" />
          </div>
          {(tab === 'all' || tab === 'unassigned' || tab === 'my_queue') && (
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-9 text-[12.5px] border border-slate-200 rounded-lg px-2 bg-white text-slate-600 focus:outline-none focus:border-blue-400"
              data-testid="typing-status-filter">
              <option value="">Status: All</option>
              <option value="pending">Pending Typing</option>
              <option value="in_progress">In Progress</option>
            </select>
          )}
          {hasFilters && (
            <button onClick={clearFilters} className="h-9 px-2.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800" data-testid="typing-clear-filters">
              Clear
            </button>
          )}
        </div>

        <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm transition-opacity ${refreshing && !loading ? 'opacity-60' : ''}`}>
          <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="typing-orders-table">
            <thead>
              <tr className="text-left text-[10.5px] text-white/95 uppercase tracking-wider border-b border-slate-200"
                style={{ background: 'linear-gradient(135deg, #003c8f 0%, #004aad 55%, #0d55b8 100%)' }}>
                <th className="px-4 py-3 w-[13%]">Order</th>
                <th className="px-2 py-3 w-[100px]">Order Date</th>
                <th className="px-2 py-3 w-[70px]">Client</th>
                <th className="px-2 py-3 w-[12%]">Product</th>
                <th className="px-2 py-3">Property Address</th>
                <th className="px-2 py-3 w-[90px]">County</th>
                <th className="px-2 py-3 w-[52px]">State</th>
                <th className="px-2 py-3 w-[92px]">Int/Ext</th>
                <th className="px-2 py-3 w-[96px]">Status</th>
                <th className="px-4 py-3 w-[13%]">Typist</th>
                <th className="px-3 py-3 w-[92px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-14 text-center text-slate-400">
                  <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin text-blue-400" /> Loading Typing queue…
                </td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-14 text-center" data-testid="typing-empty-state">
                  <span className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <EmptyIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <p className="text-sm font-semibold text-slate-600">{emptyMeta.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{emptyMeta.hint}</p>
                  {hasFilters && (
                    <button onClick={clearFilters} className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-semibold" data-testid="typing-empty-clear-filters">
                      Clear search & filters
                    </button>
                  )}
                </td></tr>
              ) : orders.map(o => {
                // Single source of truth — derived from backend typing state:
                // unassigned → Pending; claimed/started → In Progress;
                // completed/returned only after the backend operation succeeded.
                const st = o.typing_status === 'completed' ? STATUS_META.completed
                  : o.typing_status === 'returned' ? STATUS_META.returned
                  : o.typing_status === 'returned_to_qc' ? STATUS_META.returned_to_qc
                  : o.typing_assigned_to ? STATUS_META.in_progress
                  : { ...STATUS_META.pending, label: 'Pending' };
                const mine = o.typing_assigned_to === user?.id;
                return (
                  <tr key={o.id} className="border-b border-slate-100 hover:bg-blue-50/40 transition-colors" data-testid={`typing-row-${o.order_number}`}>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5">
                        <button onClick={() => setSummaryOrder(o)} className="text-[12.5px] font-semibold text-blue-700 hover:text-blue-900 hover:underline whitespace-nowrap" data-testid={`typing-order-link-${o.order_number}`}>
                          {o.order_number}
                        </button>
                        <PriorityIndicator priority={o.priority} />
                      </span>
                    </td>
                    <td className="px-2 py-3 text-[11.5px] text-slate-500 whitespace-nowrap">
                      {o.created_at ? formatDateOnlyET(o.created_at) : '—'}
                    </td>
                    <td className="px-2 py-3">
                      <span className="text-[12.5px] font-semibold text-slate-800">{o.client_code}</span>
                    </td>
                    <td className="px-2 py-3 text-[12px] text-slate-600">{o.client_product || '—'}</td>
                    <td className="px-2 py-3">
                      {(() => { const show = typingPropertyAddress(o); return (
                        <span className="block text-[12.5px] font-medium text-slate-700 truncate max-w-[320px]" title={show}>{show}</span>
                      ); })()}
                    </td>
                    <td className="px-2 py-3 text-[12px] text-slate-600 whitespace-nowrap">{o.property_county || '—'}</td>
                    <td className="px-2 py-3 text-[12px] font-semibold text-slate-700">{o.property_state || '—'}</td>
                    <td className="px-2 py-3">
                      {o.typing_is_external === true ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700" data-testid={`ext-indicator-${o.order_number}`}><Globe className="h-3 w-3" /> External</span>
                      ) : o.typing_is_external === false ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600"><Building2 className="h-3 w-3" /> Internal</span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-2 py-3">
                      <span className={`text-[10.5px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`typing-status-${o.order_number}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {o.typing_assigned_to_name ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap"><User className="h-3 w-3 text-slate-400" />{o.typing_assigned_to_name}{mine && <span className="text-[9.5px] font-bold text-blue-600">(me)</span>}</span>
                      ) : <span className="text-slate-300">Unassigned</span>}
                    </td>
                    <td className="px-3 py-3">
                      {/* TYPING-CLAIM-QUEUE-001: Claim lives on the queue only.
                          Reuses the atomic /typing/assign-to-me endpoint. */}
                      {isTypist && !o.typing_assigned_to && o.typing_status !== 'completed' && o.typing_status !== 'returned' ? (
                        <button
                          onClick={() => act(o.id, 'assign-to-me')}
                          disabled={busy === o.id + 'assign-to-me'}
                          className="px-3 py-1.5 text-[11.5px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 whitespace-nowrap"
                          data-testid={`typing-claim-btn-${o.order_number}`}
                        >
                          {busy === o.id + 'assign-to-me' ? 'Claiming…' : 'Claim'}
                        </button>
                      ) : mine && o.typing_status === 'pending' ? (
                        /* TYPING-START-DASH-001: the only place Typing is started
                           (existing /typing/start endpoint; pending → in_progress). */
                        <button
                          onClick={() => act(o.id, 'start')}
                          disabled={busy === o.id + 'start'}
                          className="px-3 py-1.5 text-[11.5px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1"
                          data-testid={`typing-start-btn-${o.order_number}`}
                        >
                          <PlayCircle className="h-3 w-3" /> {busy === o.id + 'start' ? 'Starting…' : 'Start Typing'}
                        </button>
                      ) : mine && o.typing_status !== 'completed' ? (
                        <button
                          onClick={() => navigate(`/orders/${o.id}`)}
                          className="px-3 py-1.5 text-[11.5px] font-semibold border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-md transition-colors whitespace-nowrap"
                          data-testid={`typing-open-btn-${o.order_number}`}
                        >
                          {o.typing_status === 'in_progress' ? 'Continue' : 'Open'}
                        </button>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Standard TitleFlow pagination — server-side */}
          <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/60 flex flex-wrap items-center gap-3" data-testid="typing-pagination">
            <span className="text-[12px] text-slate-500" data-testid="typing-pagination-summary">
              {count === 0 ? '0 files' : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, count)} of ${count} files`}
            </span>
            <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
              <span>Rows per page</span>
              <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="h-7 border border-slate-200 rounded-md px-1.5 bg-white text-slate-600 focus:outline-none"
                data-testid="typing-per-page-select">
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="ml-auto flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2 text-[12px]"
                  onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || refreshing}
                  data-testid="typing-pagination-prev">‹ Previous</Button>
                {pageNumbers.map(p => (
                  <button key={p} onClick={() => setPage(p)} disabled={refreshing}
                    className={`h-7 min-w-[28px] px-1.5 rounded-md text-[12px] font-semibold transition-colors ${p === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`typing-pagination-page-${p}`}>{p}</button>
                ))}
                <Button variant="outline" size="sm" className="h-7 px-2 text-[12px]"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || refreshing}
                  data-testid="typing-pagination-next">Next ›</Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Standard Order Summary modal — same component the Processor
          dashboard uses; typist stays on the queue in the background */}
      <OrderSummaryPopup
        isOpen={!!summaryOrder}
        orderData={summaryOrder}
        currentUser={user}
        onClose={() => setSummaryOrder(null)}
        onUpdate={load}
      />

      {/* TYPING-REVIEW-002 — queue Return modal removed; Return lives in Fulfillment */}

      {assignModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="typing-assign-modal">
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3">
            <h3 className="font-bold text-slate-800">{assignModal.typing_assigned_to ? 'Reassign' : 'Assign'} {assignModal.order_number}</h3>
            <select value={assignTarget} onChange={e => setAssignTarget(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2 bg-white focus:outline-none"
              data-testid="typing-assign-select">
              <option value="">Select Typing Processor…</option>
              {workload.filter(w => w.active).map(w => (
                <option key={w.user_id} value={w.user_id}>{w.name} — {w.assigned} assigned</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAssignModal(null)}>Cancel</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!assignTarget}
                onClick={() => act(assignModal.id, assignModal.typing_assigned_to ? 'reassign' : 'assign', { user_id: assignTarget })}
                data-testid="typing-assign-submit">Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
